import type { ObservationCriterion } from '@/types';

export type ChecklistEntry = {
  criterion_id: number;
  passed: boolean | null;
  notes: string;
};

type Props = {
  criteria: ObservationCriterion[];
  value: ChecklistEntry[];
  onChange: (next: ChecklistEntry[]) => void;
};

export function CriteriaChecklist({ criteria, value, onChange }: Props) {
  function update(id: number, patch: Partial<Omit<ChecklistEntry, 'criterion_id'>>) {
    const existing = value.find((v) => v.criterion_id === id);
    if (existing) {
      onChange(value.map((v) => (v.criterion_id === id ? { ...v, ...patch } : v)));
    } else {
      onChange([
        ...value,
        { criterion_id: id, passed: patch.passed ?? null, notes: patch.notes ?? '' },
      ]);
    }
  }

  if (criteria.length === 0) {
    return (
      <p className="rounded border border-dashed border-border px-3 py-4 text-center text-sm text-muted-foreground">
        Esta lección no tiene criterios configurados.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {[...criteria]
        .sort((a, b) => a.order - b.order)
        .map((c) => {
          const result = value.find((v) => v.criterion_id === c.id);
          const isAnswered = result && result.passed !== null;
          const missing = c.required && !isAnswered;
          return (
            <div
              key={c.id}
              className={`rounded border p-3 bg-card ${
                missing ? 'border-destructive' : 'border-border'
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm font-medium text-card-foreground">
                  {c.label}{' '}
                  {c.required && (
                    <span className="text-destructive" aria-label="requerido">
                      *
                    </span>
                  )}
                </span>
                <div className="flex gap-2">
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="radio"
                      name={`criterion-${c.id}`}
                      checked={result?.passed === true}
                      onChange={() => update(c.id, { passed: true })}
                    />
                    Cumple
                  </label>
                  <label className="flex items-center gap-1 text-sm">
                    <input
                      type="radio"
                      name={`criterion-${c.id}`}
                      checked={result?.passed === false}
                      onChange={() => update(c.id, { passed: false })}
                    />
                    No cumple
                  </label>
                </div>
              </div>
              <input
                type="text"
                placeholder="Nota opcional…"
                value={result?.notes ?? ''}
                onChange={(e) => update(c.id, { notes: e.target.value })}
                className="mt-2 w-full rounded border border-input bg-background px-2 py-1 text-sm"
              />
            </div>
          );
        })}
    </div>
  );
}
